import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import lightTheme from "./theme";
import { fetchOrgRequest } from "./api/organizations/orgSlice";
import { fetchSubscriptionRequest } from "./api/subscription/subscriptionSlice";
import {
  DashboardLayout,
  PageContainer,
  DashboardSidebarPageItem,
} from "@toolpad/core";
import { Navigate, Outlet, Route, Routes, useLocation } from "react-router-dom";
import UserAccount from "./components/UserAccount";
import { BusinessCenterOutlined, DashboardOutlined } from "@mui/icons-material";
import DescriptionIcon from "@mui/icons-material/Description";
import AuthenticatedRoutes from "./routes/AuthenticatedRoutes";
import GlobalSnackbar from "./components/GlobalSnackbar";
import LoginPage from "./resources/login/pages/LoginPage";
import { ReactRouterAppProvider } from "@toolpad/core/react-router";
import SideBottomBar from "./components/SideBottomBar";
import { fetchUserRequest } from "./api/user/userSlice";
import { sessionResolved } from "./api/auth/authSlice";
import { authApiClient } from "./utils/axios";
import ScreenLoader from "./components/ScreenLoader";
import ForgotPasswordPage from "./resources/forgotPassword/pages/ForgotPasswordPage";
import { loadCodeListsRequest } from "./api/codelist/codeListsSlice";
import PeopleOutlineOutlinedIcon from "@mui/icons-material/PeopleOutlineOutlined";
import ShoppingCartIcon from "@mui/icons-material/ShoppingCart";
import ReceiptLongIcon from "@mui/icons-material/ReceiptLong";
import ArticleIcon from "@mui/icons-material/Article";
import CloudUploadOutlinedIcon from "@mui/icons-material/CloudUploadOutlined";
import Inventory2OutlinedIcon from "@mui/icons-material/Inventory2Outlined";
import SignupPage from "./resources/users/pages/SignupPage";
import appConfig from "./config/appConfig";
import { loadingPrerequisites } from "./api/app/appStateSelector";
import EnvironmentBanner from "./components/EnvironmentBanner";
import { Box } from "@mui/material";
import SelectOrganizationPage from "./resources/login/pages/SelectOrganizationPage";
import { resolveSafePostAuthPath } from "./utils/authToken";
import {
  selectRequiresOrgSelection,
  selectSessionReady,
} from "./api/auth/authSlice";

const NAVIGATION = [
  {
    segment: "dashboard",
    title: "Dashboard",
    icon: <DashboardOutlined />,
  },
  {
    kind: "divider",
  },
  {
    segment: "business-profiles",
    title: "Business Profiles",
    icon: <BusinessCenterOutlined />,
  },
  {
    segment: "products",
    title: "Products/Services",
    icon: <Inventory2OutlinedIcon />,
  },
  {
    title: "Sales",
    icon: <ShoppingCartIcon />,
    children: [
      {
        segment: "customers",
        title: "Customers",
        icon: <PeopleOutlineOutlinedIcon />,
      },
      {
        segment: "sales",
        title: "Documents",
        icon: <DescriptionIcon />,
      },
    ],
  },
  {
    title: "Purchases",
    icon: <ReceiptLongIcon />,
    children: [
      {
        segment: "suppliers",
        title: "Suppliers",
        icon: <PeopleOutlineOutlinedIcon />,
      },
      {
        segment: "purchases",
        title: "Documents",
        icon: <DescriptionIcon />,
      },
    ],
  },
  {
    segment: "reporting-reconciliation",
    title: "Reporting & Reconciliation",
    icon: <ArticleIcon />,
    children: [
      {
        segment: "document-report",
        title: "Document Report",
      },
      {
        segment: "vat-returns",
        title: "VAT Returns",
      },
    ],
  },
  {
    segment: "bulk-upload",
    title: "Bulk Upload",
    icon: <CloudUploadOutlinedIcon />,
    pattern: "sales/bulk-upload{/:id}*",
  },
];

const RedirectToSelectOrganization = () => {
  const location = useLocation();

  return (
    <Navigate
      to="/select-organization"
      state={{
        redirectTo: resolveSafePostAuthPath(
          location.pathname + location.search,
        ),
      }}
      replace
    />
  );
};
// Component to handle redirect to login with original location
const RedirectToLogin = () => {
  const location = useLocation();
  return (
    <Navigate
      to="/login"
      state={{ from: location.pathname + location.search }}
      replace
    />
  );
};
const UnauthenticatedLayout = ({ isTestingEnv }) => (
  <Box
    sx={isTestingEnv && { height: "calc(100vh - 38px)", overflow: "hidden" }}
  >
    <Outlet />
  </Box>
);

function App() {
  const isAuthenticated = useSelector((state) => state.auth.isAuthenticated);
  const sessionChecked = useSelector((state) => state.auth.sessionChecked);
  const sessionReady = useSelector(selectSessionReady);
  const requiresOrgSelection = useSelector(selectRequiresOrgSelection);
  const loading = useSelector(loadingPrerequisites);
  const dispatch = useDispatch();
  const orgLoading = useSelector((state) => state.org.loading);
  const isTestingEnv =
    appConfig.appEnv === "staging" || appConfig.appEnv === "sandbox";
  const subscriptionLoading = useSelector(
    (state) => state.subscription.loading,
  );

  const [expandedModule, setExpandedModule] = useState(null);

  useEffect(() => {
    dispatch(loadCodeListsRequest());
  }, [dispatch]);

  useEffect(() => {
    authApiClient
      .get("/me", { skipSessionRedirect: true })
      .then(() => {
        dispatch(sessionResolved({ authenticated: true }));
      })
      .catch(() => {
        dispatch(sessionResolved({ authenticated: false }));
      });
  }, [dispatch]);

  useEffect(() => {
    if (sessionReady) {
      dispatch(fetchOrgRequest());
      dispatch(fetchSubscriptionRequest());
      dispatch(fetchUserRequest());
    }
  }, [dispatch, sessionReady]);

  if (
    !sessionChecked ||
    loading ||
    (sessionReady && (orgLoading || subscriptionLoading))
  ) {
    return <ScreenLoader />;
  }

  return (
    <ReactRouterAppProvider theme={lightTheme} navigation={NAVIGATION}>
      <EnvironmentBanner env={appConfig.appEnv} />
      <Routes>
        {sessionReady ? (
          <Route
            path="/*"
            element={
              <>
                <DashboardLayout
                  slots={{
                    toolbarAccount: UserAccount,
                    sidebarFooter: SideBottomBar,
                  }}
                  sx={
                    isTestingEnv && {
                      height: "calc(100vh - 38px)",
                    }
                  }
                  branding={{
                    title: "",
                    homeUrl: "/dashboard",
                    logo: (
                      <img
                        src={appConfig.brandLogoPath}
                        alt={`${appConfig.brandName} logo`}
                      />
                    ),
                  }}
                  renderPageItem={(item) => {
                    if (!item.children) {
                      return <DashboardSidebarPageItem item={item} />;
                    }

                    const isExpanded = expandedModule === item.title;

                    return (
                      <Box
                        onClick={(event) => {
                          if (event.target.closest("a")) {
                            return;
                          }

                          setExpandedModule(isExpanded ? null : item.title);
                        }}
                      >
                        <DashboardSidebarPageItem
                          item={item}
                          expanded={isExpanded}
                        />
                      </Box>
                    );
                  }}
                  defaultSidebarCollapsed={false}
                >
                  <PageContainer maxWidth={"xxl"} breadcrumbs={[]} title="">
                    <Routes>
                      <Route
                        path="/"
                        element={<Navigate to="/dashboard" replace />}
                      />
                      {AuthenticatedRoutes()}
                    </Routes>
                  </PageContainer>
                </DashboardLayout>
              </>
            }
          />
        ) : isAuthenticated && requiresOrgSelection ? (
          <Route
            element={<UnauthenticatedLayout isTestingEnv={isTestingEnv} />}
          >
            <Route
              path="/select-organization"
              element={<SelectOrganizationPage />}
            />
            <Route path="*" element={<RedirectToSelectOrganization />} />
          </Route>
        ) : (
          <Route
            element={<UnauthenticatedLayout isTestingEnv={isTestingEnv} />}
          >
            <Route path="/login" element={<LoginPage />} />
            <Route path="/signup" element={<SignupPage />} />
            <Route path="/forgot-password" element={<ForgotPasswordPage />} />
            <Route path="*" element={<RedirectToLogin />} />
          </Route>
        )}
      </Routes>
      <ScreenLoader />
      <GlobalSnackbar />
    </ReactRouterAppProvider>
  );
}

export default App;
